import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContextLazy'
import { KeyIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import { generateDeviceHash } from '../../lib/device-fingerprint'
import { supabase } from '../../lib/supabase-auth'

export const MFAVerification: React.FC = () => {
  const [verificationCode, setVerificationCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [checkingDevice, setCheckingDevice] = useState(true)
  const { verifyMFA } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    checkDeviceTrust()
  }, [])

  const checkDeviceTrust = async () => {
    try {
      // Generate device hash
      const deviceHash = await generateDeviceHash()
      const { data: { session } } = await supabase.auth.getSession()
      
      if (session && deviceHash) {
        // Check if device is trusted
        const { data, error } = await supabase.functions.invoke('should-mfa', {
          body: { 
            deviceHash: deviceHash,  // Fixed: Use camelCase to match edge function
            context: 'login'
          },
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'x-device-hash': deviceHash
          }
        })

        if (!error && data && !data.requireMFA) {  // Fixed: Check requireMFA instead of skipMFA
          // Device is trusted, redirect directly
          sessionStorage.setItem('mfa_verified', 'true')
          sessionStorage.setItem('mfa_bypass_trusted', 'true')
          console.log('✅ Trusted device detected, bypassing MFA')
          navigate('/portal/clients')
          return
        }
      }
    } catch (err) {
      console.error('Device check error:', err)
    } finally {
      // Show MFA form after check completes
      setCheckingDevice(false)
    }
  }

  const handleVerifyMFA = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    
    const { error } = await verifyMFA(verificationCode)
    
    if (error) {
      setError(error.message)
      setLoading(false)
      return
    }
    
    // Successfully verified, redirect to clients page
    navigate('/portal/clients')
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>
      
      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Logo and Branding */}
        <div className="text-center flex flex-col items-center">
          <Link to="/" className="block -ml-6">
            <img 
              className="h-32 w-auto mb-6 cursor-pointer hover:opacity-90 transition-opacity" 
              src="/assets/Brands/PCM Logo White.png" 
              alt="PCM Financial" 
            />
          </Link>
          <h2 className="text-4xl font-bold text-white mb-2">
            Two-Factor Authentication
          </h2>
          <p className="text-base text-gray-300">
            Enter the code from your authenticator app
          </p>
          <div className="w-20 h-1 bg-amber-400 mx-auto mt-6"></div>
        </div>
        
        {/* Verification Card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl p-8 transition-all duration-300">
          {checkingDevice ? (
            /* Loading State */
            <div className="flex flex-col items-center justify-center py-8">
              {/* Animated security shield */}
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-amber-100 rounded-full blur-xl animate-pulse"></div>
                <div className="relative bg-white rounded-full p-4 shadow-lg">
                  <ShieldCheckIcon className="h-12 w-12 text-amber-600" />
                </div>
                {/* Rotating ring */}
                <div className="absolute -inset-4 rounded-full border-2 border-amber-200 border-t-amber-600 animate-spin"></div>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Checking Device Security
              </h3>
              <p className="text-sm text-gray-600 mb-6 text-center">
                Verifying if this is a trusted device...
              </p>
              
              {/* Progress dots */}
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-amber-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-amber-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-amber-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          ) : (
            <div className="animate-fadeIn">
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center">
                    <ShieldCheckIcon className="h-10 w-10 text-amber-600" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <KeyIcon className="h-4 w-4 text-white" />
              </div>
            </div>
          </div>

          <form className="space-y-6" onSubmit={handleVerifyMFA}>
            <div>
              <label htmlFor="verification-code" className="block text-sm font-medium text-gray-700 mb-2 text-center">
                6-digit verification code
              </label>
              <div className="relative">
                <input
                  id="verification-code"
                  type="text"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  className="appearance-none relative block w-full px-3 py-4 bg-gray-50 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 focus:bg-white transition-all duration-200 text-center text-2xl font-mono tracking-wider"
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  autoFocus
                />
              </div>
              <p className="mt-2 text-xs text-gray-500 text-center">
                Open your authenticator app to get your code
              </p>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Verifying...
                  </div>
                ) : (
                  'Verify'
                )}
              </button>
            </div>

            <div className="text-center space-y-2">
              <p className="text-sm text-gray-600">
                Lost access to your authenticator app?
              </p>
              <Link
                to="/mfa-recovery"
                className="text-sm font-medium text-amber-600 hover:text-amber-500 transition-colors"
              >
                Recover Account Access
              </Link>
            </div>
          </form>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="text-center text-sm text-gray-400">
          <p>
            © {new Date().getFullYear()} PCM Financial. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}